import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { OrderType } from '#domain/Order.ts';
import { useFeatureParams } from '#hooks/shared/useFeatureParams.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { Filter, getOption } from '../shared/Filter.tsx';
import { FilterResetButton } from '../shared/FilterResetButton.tsx';
import { SearchInput } from '../shared/SearchInput.tsx';
import { CreateOrderButton } from './CreateOrderButton.tsx';
import { OrderStatusData } from './OrderStatusData.ts';
import type { OrderTableColumnConfig } from './OrderTable.tsx';
import { PlanOrdersButton } from './PlanOrdersButton.tsx';

const getOptions = (intlShape: IntlShape) => [
    getOption('open', OrderStatusData, intlShape),
    getOption('planned', OrderStatusData, intlShape),
    getOption('in-transit', OrderStatusData, intlShape),
    getOption('loading', OrderStatusData, intlShape),
    getOption('unloading', OrderStatusData, intlShape),
    getOption('executing', OrderStatusData, intlShape),
    getOption('finished', OrderStatusData, intlShape),
];

const getTypeOptions = (intl: IntlShape) => [
    {
        id: 'transport',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.order.type.transport',
            defaultMessage: 'Transport',
        }),
        icon: <span className='rioglyph rioglyph-truck' />,
        selected: true,
    },
    {
        id: 'service',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.order.type.service',
            defaultMessage: 'Service',
        }),
        icon: <span className='rioglyph rioglyph-wrench' />,
        selected: true,
    },
];

type OrderTableToolbarProps = {
    selectedOrders: OrderWithSortableKey[];
    onOpenCreateTourDialog: () => void;
    onOpenCreateOrderDialog: (type?: OrderType) => void;
    onOpenCsvImportDialog: () => void;
    searchInput: string;
    onSearch: (value: string) => void;
    statusFilter: string[];
    onFilterStatus: (values: string[]) => void;
    onResetStatusFilter: () => void;
    typeFilter?: string[];
    onFilterType?: (values: string[]) => void;
    onResetTypeFilter?: () => void;
    columns: OrderTableColumnConfig[];
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    onResetColumns: () => void;
    onViewTypeChange: (viewType: TableViewTogglesViewType) => void;
    viewType: TableViewTogglesViewType;
    showServiceOrders?: boolean;
};

export const OrderTableToolbar = ({
    selectedOrders,
    onOpenCreateTourDialog,
    onOpenCreateOrderDialog,
    onOpenCsvImportDialog,
    searchInput,
    onSearch,
    statusFilter,
    onFilterStatus,
    onResetStatusFilter,
    typeFilter = ['transport', 'service'],
    onFilterType = () => undefined,
    onResetTypeFilter = () => undefined,
    columns,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    onViewTypeChange,
    viewType,
    showServiceOrders = true,
}: OrderTableToolbarProps) => {
    const intl = useIntl();
    const options = useMemo(() => getOptions(intl), [intl.locale]);
    const typeOptions = useMemo(() => getTypeOptions(intl), [intl.locale]);
    const orderSelected = selectedOrders.length > 0;
    const { csvImportEnabled } = useFeatureParams();
    const showCsvImport = useFeatureToggle('showCsvImport', false);
    const showCsvImportButton = csvImportEnabled || showCsvImport;
    const createOrderTitle = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.title',
        defaultMessage: 'Create order',
    });

    return (
        <TableToolbar>
            <TableToolbarColumn>
                <ButtonToolbar>
                    <CreateOrderButton
                        id='create-order-dropdown'
                        title={createOrderTitle}
                        bsStyle={orderSelected ? Button.DEFAULT : Button.PRIMARY}
                        onSelect={onOpenCreateOrderDialog}
                    />
                    {showCsvImportButton && (
                        <Button
                            bsStyle={Button.DEFAULT}
                            iconName='rioglyph-upload'
                            autoTrackingKey='intl-msg:ordercom-cloud-frontend.orders.csv-import.action'
                            onClick={onOpenCsvImportDialog}
                        >
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.action',
                                defaultMessage: 'Import CSV',
                            })}
                        </Button>
                    )}
                    {orderSelected && (
                        <PlanOrdersButton onOpenTourDialog={onOpenCreateTourDialog} selectedOrders={selectedOrders} />
                    )}
                </ButtonToolbar>
            </TableToolbarColumn>

            <TableToolbarColumn horizontalAlign='right'>
                <FilterResetButton
                    hasActiveFilter={statusFilter.length > 0 || (showServiceOrders && typeFilter.length !== 2)}
                    onResetFilter={() => {
                        onResetStatusFilter();
                        onResetTypeFilter();
                    }}
                    trackingKey='ordercom.orders.table-toolbar.reset-filter'
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <Filter values={statusFilter} onFilter={onFilterStatus} options={options} />
            </TableToolbarColumn>
            {showServiceOrders && (
                <TableToolbarColumn horizontalAlign='right' separator>
                    <Filter values={typeFilter} onFilter={onFilterType} options={typeOptions} />
                </TableToolbarColumn>
            )}
            <TableToolbarColumn horizontalAlign='right' separator>
                <SearchInput value={searchInput} onSearch={onSearch} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableViewToggles
                    viewType={viewType}
                    onViewTypeChange={onViewTypeChange}
                    tableViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.table',

                        defaultMessage: 'Table view',
                    })}
                    singleCardViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.list',

                        defaultMessage: 'List view',
                    })}
                    multiCardsViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.cards',

                        defaultMessage: 'Cards view',
                    })}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableColumnsDropdown
                    columns={columns}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.label',

                        defaultMessage: 'Columns',
                    })}
                    onColumnOrderChange={onColumnOrderChange}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    onReset={onResetColumns}
                    showResetButton
                    resetLabel={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.reset',

                        defaultMessage: 'Reset columns',
                    })}
                />
            </TableToolbarColumn>
        </TableToolbar>
    );
};
